import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import enEquipment from './locales/en/equipment.json';
import enEquipmentManagement from './locales/en/equipmentManagement.json';
import enEquipmentLogbook from './locales/en/equipmentLogbook.json';
import enEquipmentDefects from './locales/en/equipmentDefects.json';
// equipmentModals namespace is split across per-modal files (Phase 2 parallelism).
// Each modal gets its own JSON file under locales/{lang}/equipmentModals/ and we
// merge them into a single namespace at i18n init.
import enModalAssignment from './locales/en/equipmentModals/assignment.json';
import enModalConfirmation from './locales/en/equipmentModals/confirmation.json';
import enModalEquipment from './locales/en/equipmentModals/equipment.json';
import enModalEquipmentCheck from './locales/en/equipmentModals/equipmentCheck.json';
import enModalEquipmentType from './locales/en/equipmentModals/equipmentType.json';
import enModalGroup from './locales/en/equipmentModals/group.json';
import enModalHistory from './locales/en/equipmentModals/history.json';
import enModalImportReport from './locales/en/equipmentModals/importReport.json';
import enModalInspection from './locales/en/equipmentModals/inspection.json';
import enModalInspectionNotification from './locales/en/equipmentModals/inspectionNotification.json';
import enModalModel from './locales/en/equipmentModals/model.json';
import enModalService from './locales/en/equipmentModals/service.json';
import enSettings from './locales/en/settings.json';
import enValidation from './locales/en/validation.json';
import enToasts from './locales/en/toasts.json';
import enWarehouseInventory from './locales/en/warehouseInventory.json';
import enWarehouseHistory from './locales/en/warehouseHistory.json';
import enWarehouseSettings from './locales/en/warehouseSettings.json';

import plCommon from './locales/pl/common.json';
import plEquipment from './locales/pl/equipment.json';
import plEquipmentManagement from './locales/pl/equipmentManagement.json';
import plEquipmentLogbook from './locales/pl/equipmentLogbook.json';
import plEquipmentDefects from './locales/pl/equipmentDefects.json';
import plModalAssignment from './locales/pl/equipmentModals/assignment.json';
import plModalConfirmation from './locales/pl/equipmentModals/confirmation.json';
import plModalEquipment from './locales/pl/equipmentModals/equipment.json';
import plModalEquipmentCheck from './locales/pl/equipmentModals/equipmentCheck.json';
import plModalEquipmentType from './locales/pl/equipmentModals/equipmentType.json';
import plModalGroup from './locales/pl/equipmentModals/group.json';
import plModalHistory from './locales/pl/equipmentModals/history.json';
import plModalImportReport from './locales/pl/equipmentModals/importReport.json';
import plModalInspection from './locales/pl/equipmentModals/inspection.json';
import plModalInspectionNotification from './locales/pl/equipmentModals/inspectionNotification.json';
import plModalModel from './locales/pl/equipmentModals/model.json';
import plModalService from './locales/pl/equipmentModals/service.json';
import plSettings from './locales/pl/settings.json';
import plValidation from './locales/pl/validation.json';
import plToasts from './locales/pl/toasts.json';
import plWarehouseInventory from './locales/pl/warehouseInventory.json';
import plWarehouseHistory from './locales/pl/warehouseHistory.json';
import plWarehouseSettings from './locales/pl/warehouseSettings.json';

const enEquipmentModals = {
  assignment: enModalAssignment,
  confirmation: enModalConfirmation,
  equipment: enModalEquipment,
  equipmentCheck: enModalEquipmentCheck,
  equipmentType: enModalEquipmentType,
  group: enModalGroup,
  history: enModalHistory,
  importReport: enModalImportReport,
  inspection: enModalInspection,
  inspectionNotification: enModalInspectionNotification,
  model: enModalModel,
  service: enModalService,
};

const plEquipmentModals = {
  assignment: plModalAssignment,
  confirmation: plModalConfirmation,
  equipment: plModalEquipment,
  equipmentCheck: plModalEquipmentCheck,
  equipmentType: plModalEquipmentType,
  group: plModalGroup,
  history: plModalHistory,
  importReport: plModalImportReport,
  inspection: plModalInspection,
  inspectionNotification: plModalInspectionNotification,
  model: plModalModel,
  service: plModalService,
};

const LANGUAGE_STORAGE_KEY = 'language_preference';
const SUPPORTED_LANGUAGES = ['en', 'pl'];
const DEFAULT_LANGUAGE = 'en';

const getInitialLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch (e) {
    // localStorage unavailable (SSR / privacy mode) — fall through
  }
  return DEFAULT_LANGUAGE;
};

const resources = {
  en: {
    common: enCommon,
    equipment: enEquipment,
    equipmentManagement: enEquipmentManagement,
    equipmentLogbook: enEquipmentLogbook,
    equipmentDefects: enEquipmentDefects,
    equipmentModals: enEquipmentModals,
    settings: enSettings,
    validation: enValidation,
    toasts: enToasts,
    warehouseInventory: enWarehouseInventory,
    warehouseHistory: enWarehouseHistory,
    warehouseSettings: enWarehouseSettings,
  },
  pl: {
    common: plCommon,
    equipment: plEquipment,
    equipmentManagement: plEquipmentManagement,
    equipmentLogbook: plEquipmentLogbook,
    equipmentDefects: plEquipmentDefects,
    equipmentModals: plEquipmentModals,
    settings: plSettings,
    validation: plValidation,
    toasts: plToasts,
    warehouseInventory: plWarehouseInventory,
    warehouseHistory: plWarehouseHistory,
    warehouseSettings: plWarehouseSettings,
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: [
      'common',
      'equipment',
      'equipmentManagement',
      'equipmentLogbook',
      'equipmentDefects',
      'equipmentModals',
      'settings',
      'validation',
      'toasts',
      'warehouseInventory',
      'warehouseHistory',
      'warehouseSettings',
    ],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
    returnEmptyString: false,
  });

export const changeLanguage = (lng) => {
  if (!SUPPORTED_LANGUAGES.includes(lng)) return;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch (e) {
    // ignore
  }
  i18n.changeLanguage(lng);
};

export { LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };
export default i18n;
